import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VideoPlayer from './VideoPlayer';
import { useNotification } from '../common';
import DOMPurify from 'dompurify';

/**
 * StudentVideoCourses component for students to access their video courses
 * Shows only courses the student has access to via their deal
 * Displays videos one at a time with navigation controls
 */
const StudentVideoCourses = ({ dealId }) => {
  const { showNotification } = useNotification();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  
  // Fetch courses that the student has access to
  useEffect(() => {
    const fetchStudentCourses = async () => {
      if (!dealId) {
        setError('No deal ID provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication token not found');
          setLoading(false);
          return;
        }
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // First, fetch the deal to get the course_id
        const dealResponse = await axios.get(`/api/deals/${dealId}`, config);
        
        if (!dealResponse.data.course_id) {
          setError('No course associated with this deal');
          setLoading(false);
          return;
        }
        
        // Then fetch the course details
        const courseResponse = await axios.get(`/api/settings/courses/${dealResponse.data.course_id}`, config);
        
        if (!courseResponse.data) {
          setError('Course not found');
          setLoading(false);
          return;
        }
        
        // Check if the course has video content
        if (!courseResponse.data.video_content || !courseResponse.data.video_sources) {
          setError(`The course "${courseResponse.data.name}" does not have video content available yet. Please check back later or contact our support team.`);
          setLoading(false);
          return;
        }
        
        // Set the courses array with just this course
        setCourses([courseResponse.data]);
        setSelectedCourse(courseResponse.data);
        
        // Get progress for this course
        const progressResponse = await axios.get(
          `/api/video-courses/${courseResponse.data.id}/progress/${dealId}`,
          config
        );
        
        // Update video sources with progress data
        if (progressResponse.data && progressResponse.data.length > 0) {
          const updatedCourse = { ...courseResponse.data };
          
          if (updatedCourse.video_sources) {
            updatedCourse.video_sources = updatedCourse.video_sources.map(source => {
              const progress = progressResponse.data.find(p => p.video_id === source.id);
              
              if (progress) {
                return {
                  ...source,
                  progress: {
                    position: progress.current_position,
                    percentage: progress.completion_percentage,
                    completed: progress.is_completed
                  }
                };
              }
              
              return source;
            });
          }
          
          setCourses([updatedCourse]);
          setSelectedCourse(updatedCourse);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching student courses:', err);
        setError('Failed to load courses: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };
    
    fetchStudentCourses();
  }, [dealId]);
  
  const handleProgressUpdate = (videoId, progressData) => {
    // Update local course data to reflect progress
    if (selectedCourse && selectedCourse.video_sources) {
      const updatedSources = selectedCourse.video_sources.map(source => {
        if (source.id === videoId) {
          return { ...source, progress: progressData };
        }
        return source;
      });
      
      const updatedCourse = {
        ...selectedCourse,
        video_sources: updatedSources
      };
      
      setCourses([updatedCourse]);
      setSelectedCourse(updatedCourse);
    }
  };
  
  const goToNextVideo = () => {
    if (selectedCourse?.video_sources && currentVideoIndex < selectedCourse.video_sources.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };
  
  const goToPreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };
  
  if (loading) {
    return <div className="text-center p-8">Loading your courses...</div>;
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        {error}
      </div>
    );
  }
  
  if (courses.length === 0) {
    return (
      <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-xl font-medium text-gray-700 mb-2">No Video Courses Available</h3>
        <p className="text-gray-500 mb-4">
          You don't currently have access to any video courses.
        </p>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 max-w-md mx-auto">
          <h4 className="font-semibold mb-2">Why am I seeing this?</h4>
          <ul className="list-disc pl-5 text-left space-y-1">
            <li>Your purchase may not include video content access</li>
            <li>Your account might not be properly linked to your purchase</li>
            <li>The video content may not be available yet</li>
          </ul>
          <p className="mt-3">
            Please contact our support team if you believe you should have access to video content.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-2">{selectedCourse.name}</h2>
      
      {selectedCourse.description && (
        <div className="text-gray-600 mb-6">{selectedCourse.description}</div>
      )}
      
      {selectedCourse.video_sources && selectedCourse.video_sources.length > 0 ? (
        <div>
          {/* Video navigation and progress */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-500">
              Lesson {currentVideoIndex + 1} of {selectedCourse.video_sources.length}
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={goToPreviousVideo} 
                disabled={currentVideoIndex === 0}
                className={`px-3 py-1 rounded ${
                  currentVideoIndex === 0 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                Previous
              </button>
              <button 
                onClick={goToNextVideo} 
                disabled={currentVideoIndex >= selectedCourse.video_sources.length - 1}
                className={`px-3 py-1 rounded ${
                  currentVideoIndex >= selectedCourse.video_sources.length - 1 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Next Lesson
              </button>
            </div>
          </div>
          
          {/* Current video */}
          <div className="bg-gray-50 p-4 rounded-lg">
            {(() => {
              const video = selectedCourse.video_sources[currentVideoIndex];
              return (
                <>
                  <h3 className="text-lg font-medium mb-3">
                    {video.title || `Video ${currentVideoIndex + 1}`}
                  </h3>
                  
                  {video.description && (
                    <div 
                      className="text-gray-600 mb-4"
                      style={{
                        lineHeight: '1.6',
                        fontSize: '14px'
                      }}
                    >
                      {/* Show both raw and processed for debugging */}
                      <details style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
                        <summary>Debug: Click to see raw HTML</summary>
                        <pre style={{ background: '#f5f5f5', padding: '8px', fontSize: '10px', overflow: 'auto' }}>
                          {JSON.stringify(video.description, null, 2)}
                        </pre>
                      </details>
                      
                      <div
                        className="rich-text-content"
                        dangerouslySetInnerHTML={{ 
                          __html: video.description
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&amp;/g, '&')
                            .replace(/&quot;/g, '"')
                            .replace(/&#x27;/g, "'")
                        }}
                      />
                      
                      <style jsx>{`
                        .rich-text-content p {
                          margin-bottom: 1em;
                        }
                        .rich-text-content ul, .rich-text-content ol {
                          margin-left: 1.5em;
                          margin-bottom: 1em;
                        }
                        .rich-text-content li {
                          margin-bottom: 0.5em;
                        }
                        .rich-text-content strong, .rich-text-content b {
                          font-weight: bold;
                        }
                        .rich-text-content em, .rich-text-content i {
                          font-style: italic;
                        }
                        .rich-text-content u {
                          text-decoration: underline;
                        }
                        .rich-text-content a {
                          color: #3b82f6;
                          text-decoration: underline;
                        }
                        .rich-text-content a:hover {
                          color: #1d4ed8;
                        }
                      `}</style>
                    </div>
                  )}
                  
                  <VideoPlayer
                    courseId={selectedCourse.id}
                    dealId={dealId}
                    videoId={video.id || `video-${currentVideoIndex}`}
                    videoUrl={video.url}
                    onProgressUpdate={(progress) => handleProgressUpdate(video.id || `video-${currentVideoIndex}`, progress)}
                  />
                  
                  {video.progress && (
                    <div className="mt-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {video.progress.completed ? (
                            <span className="text-green-600 font-medium">✓ Completed</span>
                          ) : (
                            <span>{video.progress.percentage}% complete</span>
                          )}
                        </span>
                        <span className="text-gray-500">
                          Last updated: {new Date().toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          
          {/* Video list/progress overview */}
          <div className="mt-6">
            <h4 className="text-md font-medium mb-3">Course Progress</h4>
            <ul className="space-y-2">
              {selectedCourse.video_sources.map((video, index) => (
                <li 
                  key={video.id || index} 
                  className={`p-2 rounded cursor-pointer ${
                    index === currentVideoIndex 
                      ? 'bg-blue-100 border-l-4 border-blue-600' 
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setCurrentVideoIndex(index)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <span className="font-medium">{video.title || `Lesson ${index + 1}`}</span>
                      {!video.url && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Text Only
                        </span>
                      )}
                    </div>
                    <div className="text-sm">
                      {video.progress?.completed ? (
                        <span className="text-green-600">✓ Completed</span>
                      ) : video.progress?.percentage > 0 ? (
                        <span className="text-yellow-600">{video.progress.percentage}% complete</span>
                      ) : (
                        <span className="text-gray-400">Not started</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">
            This course does not have any video content available yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default StudentVideoCourses;